import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { useClickOutside } from 'hooks/click-outside';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarIcon, CalendarPlus } from 'assets/icons';
import { getDateRange } from 'helpers/utils';
import { PositionType } from 'common/menu';
import styles from './date-picker.module.scss';
import { DateMenu } from './components/date-menu';

interface DatePickerProps {
  startDate?: string;
  endDate?: string;
  menuPosition?: PositionType;
  onUpdateDate: (startDate: string, endDate: string) => void;
  closeRef?: React.MutableRefObject<(() => void) | null>;
  onMenuOpen?: () => void;
  // What the date belongs to (see DateMenu).
  subject?: 'album' | 'memory';
}

export const DatePicker = ({
  startDate,
  endDate,
  menuPosition,
  onUpdateDate,
  closeRef,
  onMenuOpen,
  subject,
}: DatePickerProps) => {
  const dateMenuRef = useRef<HTMLDivElement>(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [startDateState, setStartDateState] = useState(startDate);
  const [endDateState, setEndDateState] = useState(endDate);
  useClickOutside(dateMenuRef, () => setIsDateModalOpen(false));

  const closeDateMenu = useCallback(() => {
    setIsDateModalOpen(false);
  }, []);

  useEffect(() => {
    if (closeRef) {
      const ref = closeRef;
      ref.current = closeDateMenu;
    }
  }, [closeRef, closeDateMenu]);

  const handleUpdateDate = (newStartDate: string, newEndDate: string) => {
    setStartDateState(newStartDate);
    setEndDateState(newEndDate);
    onUpdateDate(newStartDate, newEndDate);
    setIsDateModalOpen(false);
  };

  const dateRange = getDateRange(startDateState, endDateState);
  return (
    <div className={styles.positionRelative} ref={dateMenuRef}>
      <Button
        buttonStyle={ButtonStyle.TertiaryGray}
        buttonSize={ButtonSize.Small}
        className={styles.tagButton}
        onClick={() => {
          if (isDateModalOpen) {
            setIsDateModalOpen(false);
          } else {
            onMenuOpen?.();
            setIsDateModalOpen(true);
          }
        }}
      >
        {startDateState ? <CalendarIcon /> : <CalendarPlus />}
        {dateRange.startDate && (
          <span className="text__body__semi__bold__small__gray600">
            {dateRange.startDate}
            {dateRange.endDate && dateRange.endDate !== dateRange.startDate
              ? ` - ${dateRange.endDate}`
              : ''}
          </span>
        )}
      </Button>
      {isDateModalOpen && (
        <DateMenu
          subject={subject}
          currentDate={startDateState}
          currentFinalDate={endDateState}
          menuPosition={menuPosition}
          onUpdateDate={handleUpdateDate}
        />
      )}
    </div>
  );
};
